import toastr from 'toastr'

import { mapLoggedInAndImpersonatedUserFromApi } from '@mappers/user'

import UserService from '@/services/user'
import UserModel from '@/models/user/user'
import QuickSetupStep from '@/models/user/quickSetupStep'
import UserDetailsModel from '@/models/user/userDetails'
import { UserStore } from '@/models/user/userStore'
import { MerchantPermissions } from '@/rbac'
import KYCStatus from '@/enums/modules/setup/KYCStatus'
import PersonaClient from '@/components/dashboard/panels/KYC/persona.js'

export const user = {
  namespaced: true,
  state: {
    user: new UserModel(),
    impersonatedUser: new UserDetailsModel(),
    loggedInUser: new UserDetailsModel(),
    accessToken: null,
    oneIdClaim: null,
    externalIdInfo: {
      resourceName: null,
      isPoweredBySubAccountUser: false
    },
    quickSetupSteps: [],
    landingPageEmailGuid: null,
    userStores: [],
    abilities: {
      can: () => false
    },
    kyc: null,
    isActionsBlockedByKYC: false
  },
  mutations: {
    setAbilities (state, abilities) {
      state.abilities = abilities
    },
    setAgreedToTerms (state, val) {
      state.user.agreedToTerms = val
    },
    setCompanyName (state, val) {
      state.user.companyName = val
    },
    setCompanyWebsite (state, val) {
      state.user.companyWebsite = val
    },
    setWorkingWithSales (state, val) {
      state.user.workingWithSales = val
    },
    setFirstName (state, val) {
      state.user.firstName = val
    },
    setLastName (state, val) {
      state.user.lastName = val
    },
    setEmail (state, val) {
      state.user.email = val
    },
    setPhoneNumber (state, val) {
      state.user.phoneNumber = val
    },
    setTotalOrdersPerMonth (state, val) {
      state.user.totalOrdersPerMonth = val
    },
    setPassword (state, val) {
      state.user.password = val
    },
    setConfirmPassword (state, val) {
      state.user.confirmPassword = val
    },
    setVerifiedRecaptcha (state, val) {
      state.user.verifiedRecaptcha = val
    },
    setEmployeeSignUpToken (state, token) {
      state.user.employeeSignupToken = token
    },
    resetUser (state) {
      state.user = new UserModel()
    },
    setUser (state, user) {
      state.user = new UserModel(user)
    },
    setLoggedInUserOrgKyc (state, { kyc, attempts }) {
      state.kyc = kyc
        ? {
            inquiryId: kyc?.inquiry_id,
            currentKYCStatus: kyc?.inquiry_status_id,
            currentKYCSession: kyc?.session_id,
            inquiryTypeId: kyc?.inquiry_type_id,
            completionKYCDeadline: kyc?.completion_deadline,
            attempts
          }
        : null
    },
    setImpersonatedUser (state, user) {
      state.impersonatedUser = new UserDetailsModel(user)
    },
    setLoggedInUser (state, user) {
      state.loggedInUser = new UserDetailsModel(user)
    },
    setOneIdClaim (state, oneIdClaim) {
      state.oneIdClaim = oneIdClaim
    },
    setAccessToken (state, accessToken) {
      state.accessToken = accessToken
    },
    setExternalIdInfo (state, { oneIdClaim }) {
      const claims = oneIdClaim

      if (claims?.org_atts) {
        const isPoweredBySubAccountUser = !!claims.org_atts.parent_organization_id &&
                                          claims.org_atts.parent_organization_id !== '00000000-0000-0000-0000-000000000000' &&
                                          (claims.org_atts.external_type_id === '1')

        state.externalIdInfo.resourceName = isPoweredBySubAccountUser ? claims.org_atts.resource_id : null
        state.externalIdInfo.isPoweredBySubAccountUser = isPoweredBySubAccountUser

        window.localStorage.setItem('auth_oneIdClaim', JSON.stringify(claims))
      }
    },
    setUserStep (state, step) {
      state.user.lastCompletedStep = step
    },
    setQuickSetupSteps (state, steps) {
      state.quickSetupSteps = steps
    },
    setPreLaunchUser (state, preLaunchUser) {
      state.user.preLaunchUser = preLaunchUser
    },
    setLandingPageEmailGuid (state, guid) {
      state.landingPageEmailGuid = guid
    },
    setECommercePlatform (state, val) {
      state.user.eCommercePlatform = val
    },
    setEmailOptIn (state, val) {
      state.user.emailOptIn = val
    },
    setUtmSource (state, val) {
      state.user.utmSource = val
    },
    setUserStores (state, userStores) {
      state.userStores = userStores
    },
    setIsActionsBlockedByKYC (state, isActionsBlockedByKYC) {
      state.user.isActionsBlockedByKYC = isActionsBlockedByKYC
      window.localStorage.setItem('isActionsBlockedByKYC', isActionsBlockedByKYC)
    }
  },
  actions: {
    setAgreedToTerms (context, val) {
      context.commit('setAgreedToTerms', val)
    },
    setCompanyName (context, val) {
      context.commit('setCompanyName', val)
    },
    setCompanyWebsite (context, val) {
      context.commit('setCompanyWebsite', val)
    },
    setWorkingWithSales (context, val) {
      context.commit('setWorkingWithSales', val)
    },
    setFirstName (context, val) {
      context.commit('setFirstName', val)
    },
    setLastName (context, val) {
      context.commit('setLastName', val)
    },
    setEmail (context, val) {
      context.commit('setEmail', val)
    },
    setPhoneNumber (context, val) {
      context.commit('setPhoneNumber', val)
    },
    setTotalOrdersPerMonth (context, val) {
      context.commit('setTotalOrdersPerMonth', val)
    },
    setPassword (context, val) {
      context.commit('setPassword', val)
    },
    setConfirmPassword (context, val) {
      context.commit('setConfirmPassword', val)
    },
    setEmployeeSignUpToken (context, token) {
      context.commit('setEmployeeSignUpToken', token)
    },
    setPreLaunchUser (context, val) {
      context.commit('setPreLaunchUser', val)
    },
    setECommercePlatform (context, val) {
      context.commit('setECommercePlatform', val)
    },
    setEmailOptIn (context, val) {
      context.commit('setEmailOptIn', val)
    },
    setUtmSource (context, val) {
      context.commit('setUtmSource', val)
    },
    resetUser (context) {
      context.commit('resetUser')
    },
    async verifyRecaptcha (context, payload) {
      let reset = false
      let res = false
      if (typeof payload === 'object') reset = payload.reset
      if (!reset) res = await UserService.verifyRecaptcha(payload)
      context.commit('setVerifiedRecaptcha', res.Success)
      return res
    },
    async register (context, payload) {
      const response = await UserService.register(payload)
      const success = response.Success
      if (!success) {
        toastr.error(response.Error)
      }
      return response
    },
    async forgotPassword (context, payload) {
      const resp = await UserService.forgotPassword(payload)
      return resp === ''
    },
    async newPassword (context, payload) {
      const user = await UserService.newPassword(payload)
      const defaultError = 'The Reset Link has expired. Please click Forgot Password again.'
      let errMessage = user.Error || defaultError
      // TODO : change the endpoint to return Bool not String.
      switch (user.Success) {
        case true:
          return true
        case false :
          if (errMessage.includes('Invalid token')) {
            errMessage = defaultError
          }
          toastr.error(errMessage)
          return false
        default :
          toastr.error(errMessage)
          return false
      }
    },
    async login (context, payload) {
      const user = await UserService.login(payload)
      const success = user ? user.Success : false
      if (!success) toastr.error(user.PayLoad || 'Incorrect Email or Password.')
      context.commit('setAuthentication', success, { root: true })
      context.commit('setUser', user)
      return success
    },
    async saveToken (context, payload) {
      const user = await UserService.saveToken(payload)
      const success = user ? user.Success : false
      if (!success) toastr.error(user.PayLoad.Message || 'Error occurred.')
      context.commit('setAuthentication', success, { root: true })
      context.commit('setUser', { Success: success, PayLoad: user.PayLoad.Message, Error: user.Error })
      return user
    },
    async saveRefreshedToken (context, payload) {
      const user = await UserService.saveRefreshedToken(payload)
      const success = user ? user.Success : false
      if (!success) toastr.error(user.PayLoad || 'Error occurred.')
      return success
    },
    updateToken (context, payload) {
      context.commit('setAccessToken', payload)
    },
    async logout (context, payload) {
      const success = await UserService.logout(payload)
      if (!success) toastr.error('Logout Error.')
      return success
    },
    async clearAuth (context, payload) {
      const success = await UserService.clearAuth(payload)
      if (!success) toastr.error('Logout Error.')
      return success
    },
    async getUserOrgKYC (context) {
      const response = await UserService.getKYCStatus()
      const latest = response?.latest_inquiry

      if (latest?.inquiry_status_id === KYCStatus.Completed || latest?.inquiry_status_id === KYCStatus.Pending || latest?.inquiry_status_id === KYCStatus.NeedsReview) {
        setTimeout(async () => {
          await context.dispatch('getUserOrgKYC')
        }, 10000)
      }

      context.commit('setLoggedInUserOrgKyc', { kyc: latest, attempts: response?.attempts })
    },
    async getUser (context, payload) {
      const user = context.state.user
      const preLaunchUser = context.state.user.preLaunchUser
      if (user.isDefault() || !user.impersonatedUser || user.impersonatedUser.isDefault()) {
        const [loggedInUserDetails, impersonatedUserDetails] = await Promise.all([
          UserService.getDetailedUser(),
          UserService.getImpersonatedDetailedUserData(),
          context.dispatch('getUserOrgKYC'),
          context.dispatch('setupFrontEndAppSettings', {}, { root: true }),
          context.dispatch('accountManager/getAccountManager', {}, { root: true }),
          context.dispatch('fetchUserHomeCountry', {}, { root: true })
        ])

        if (process.env.USE_PHOTOPRINT === '1') {
          context.dispatch('getQuickSetupSteps')
        }

        await context.dispatch('getAdditionalUserData', { loggedInUserDetails, impersonatedUserDetails })

        const newUser = {
          ImpersonatedUser: impersonatedUserDetails,
          LoggedInUser: loggedInUserDetails
        }

        const mappedUser = mapLoggedInAndImpersonatedUserFromApi({ ...newUser, preLaunchUser })
        context.commit('setUser', mappedUser)

        if (newUser.LoggedInUser) {
          window.localStorage.setItem('ShowUserManagement', newUser.LoggedInUser.HasMultipleOrganizations)
        }
        return user.onboardingStatus()
      }
      return true
    },
    async getAdditionalUserData (context, { loggedInUserDetails, impersonatedUserDetails }) {
      if (!loggedInUserDetails) {
        loggedInUserDetails = await UserService.getDetailedUser()
      }

      if (!impersonatedUserDetails) {
        impersonatedUserDetails = await UserService.getImpersonatedDetailedUserData()
      }

      if (loggedInUserDetails) {
        context.commit('setLoggedInUser', loggedInUserDetails)
        context.commit('setOneIdClaim', loggedInUserDetails.OneIdClaim)
        MerchantPermissions.create(loggedInUserDetails?.OneIdClaim?.Perm)
        context.commit('setExternalIdInfo', {
          oneIdClaim: loggedInUserDetails.OneIdClaim
        })
      }
      if (impersonatedUserDetails) { context.commit('setImpersonatedUser', impersonatedUserDetails) }
    },
    async setUserStep (context, step, retryCount = 0) {
      if (retryCount > 3) {
        return { Success: true }
      }
      let success = await UserService.setUserStep(step)
      if (!success || !success.Success) {
        success = this.setUserStep(context, step, retryCount + 1)
      }
      if (success) context.commit('setUserStep', step)
      return success
    },
    async setIsActionsBlockedByKYC (context, isActionsBlockedByKYC) {
      context.commit('setIsActionsBlockedByKYC', isActionsBlockedByKYC)
    },
    async upsertLandingPageEmail (context) {
      const response = await UserService.upsertLandingPageEmail({
        Id: context.state.landingPageEmailGuid,
        Email: context.state.user.email.value
      })
      context.commit('setLandingPageEmailGuid', response)
    },
    async removeLandingPageEmail (context) {
      if (!context.state.landingPageEmailGuid) return
      UserService.removeLandingPageEmail({
        Id: context.state.landingPageEmailGuid
      })
    },
    async getQuickSetupSteps (context) {
      const response = await UserService.getQuickSetupSteps()
      if (response && response?.data) {
        const quickSetupSteps = []
        response?.data.forEach(step => {
          quickSetupSteps.push(new QuickSetupStep(step))
        })
        context.commit('setQuickSetupSteps', quickSetupSteps)
      }
    },
    async getUserStores (context) {
      const response = await UserService.getUserStores()
      if (response) {
        const userStores = []
        response.forEach(s => {
          userStores.push(new UserStore(s))
        })
        context.commit('setUserStores', userStores)
      }
    },
    async triggerKYCAction (context) {
      if (!context.state.impersonatedUser) return
      const { inquiryId, currentKYCSession, currentKYCStatus } = context.state.kyc

      if (currentKYCStatus === KYCStatus.Expired) {
        await UserService.reinitiateKyc()
        context.dispatch('getUserOrgKYC')
      } else {
        PersonaClient.init(context.state.impersonatedUser._userId, inquiryId, currentKYCSession).open()
      }
    }
  },
  getters: {
    getUserOrgKYC: state => state.kyc,
    getAbilities: state => state.abilities,
    getUser: state => state.user,
    getLoggedInUser: state => state.loggedInUser,
    getOneIdClaim: state => state.oneIdClaim,
    getAccessToken: state => state.accessToken,
    getExternalIdInfo: state => state.externalIdInfo,
    getImpersonatedUser: state => state.impersonatedUser,
    getUserForAnalytics: state => state.user.mapForAnalytics(),
    getUserForAdminPage: state => state.user.mapForAdminPage(),
    getQuickSetupSteps: state => state.quickSetupSteps,
    getUserStores: state => state.userStores,
    isDemoUser: state => state.user.impersonatedRoleId === 14,
    isAdmin: state => state.user.role === 9,
    isActionsBlockedByKYC: state => state.user.isActionsBlockedByKYC
  }
}
